// Regras de cálculo do rateio de faturamento entre SPEs.
// Fonte da verdade única: a UI e o adaptador mockado consomem daqui, para que a
// regra de arredondamento não exista em dois lugares.

export interface IUniformShare {
  weight: number;
  isRoundingAdjusted: boolean;
}

export const round2 = (value: number): number => Math.round(value * 100) / 100;

// Distribui 100% em partes iguais entre `count` SPEs.
//
// 100 raramente divide exato (3 SPEs = 33,333...). Arredondar para baixo deixaria
// o total em 99,99% e quebraria a regra de soma; esconder a diferença criaria
// centavos que ninguém consegue rastrear na conferência da nota.
//
// Regra determinística: todas recebem o piso com duas casas, e o resto vai
// integralmente para a ÚLTIMA SPE da lista, marcada como ajustada.
// Ex.: 3 SPEs = 33,33 / 33,33 / 33,34.
export const distributeUniformWeights = (count: number): IUniformShare[] => {
  if (count <= 0) {
    return [];
  }

  const base = Math.floor((100 / count) * 100) / 100;
  const remainder = round2(100 - base * count);

  return Array.from({ length: count }, (_, index) => {
    const isLast = index === count - 1;
    return {
      weight: isLast ? round2(base + remainder) : base,
      isRoundingAdjusted: isLast && remainder > 0,
    };
  });
};

export const sumWeights = (weights: number[]): number =>
  round2(weights.reduce((total, weight) => total + weight, 0));

// Aceita o formato que o usuário digita em pt-BR ("45,5", "45.5", "45").
// Retorna null quando não dá para interpretar — o chamador decide o que fazer.
export const parseWeight = (value: string): number | null => {
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');

  if (normalized === '') {
    return null;
  }

  if (!/^\d*\.?\d*$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? round2(parsed) : null;
};

export const formatWeight = (value: number): string =>
  value.toFixed(2).replace('.', ',');

// Valor em reais que cabe a um peso, sobre o preço do plano.
export const weightToAmount = (weight: number, planPrice?: number): number | undefined =>
  planPrice === undefined ? undefined : round2((planPrice * weight) / 100);
